import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { FlagType } from '@af/jira-flags-types';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { useIsEditionAwarenessPillDismissed } from '@atlassian/jira-edition-awareness-for-standard-to-premium/src/services/is-ea-pill-dismissed/index.tsx';
import { useIsEligibleForUpsellEa } from '@atlassian/jira-edition-awareness-for-standard-to-premium/src/services/is-eligible-for-upsell-ea/index.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import {
	ContextualAnalyticsData,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-site-admin/index.tsx';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import {
	EditionAwareness as EditionAwarenessImpl,
	type EditionAwarenessProps,
	type Flag,
	type UserRole,
} from '@atlassian/edition-awareness';
import { useFlagsService } from '@atlassian/jira-flags';
import { RenderOnClient } from '@atlassian/jira-ssr-toolkit/src/render-on-client.tsx';
import { EditionAwarenessUpsell } from '@atlassian/edition-awareness/ui/edition-awareness-upsell';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { memoizedFetchSitePersonalization } from '@atlassian/jira-personalization-service/src/services/site/index.tsx';
import type { Trait } from '@atlassian/jira-personalization-service/src/types.tsx';

import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useEnvironment } from '@atlassian/jira-tenant-context-controller/src/components/environment/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { isFedRamp } from '@atlassian/atl-context';
import { useProductAndEdition } from '../services/use-product-and-edition.tsx';
import { useReverseTrials } from '../services/use-reverse-trials.tsx';

const EditionAwarenessPlatformInnerWithRTCheck = (props: EditionAwarenessProps) => {
	const { isReady, isInReverseTrial, onOpenOnboardingMenu } = useReverseTrials();

	// We need to wait for the reverse trial check to be complete before rendering the button
	const canRender = props.canRender && isReady;

	return (
		<EditionAwarenessImpl
			{...props}
			isInReverseTrial={isInReverseTrial}
			onOpenOnboardingMenu={onOpenOnboardingMenu}
			canRender={canRender}
		/>
	);
};

const EditionAwarenessPlatformInner = (props: EditionAwarenessProps) => {
	const { isDismissed, handleDismiss } = useIsEditionAwarenessPillDismissed(props.product);
	const { isEligibleForUpsellEa } = useIsEligibleForUpsellEa({
		product: props.product,
		edition: props.edition,
		isSiteAdmin: Boolean(props.userRoles?.includes('SITE_ADMIN')),
	});

	return isEligibleForUpsellEa ? (
		<EditionAwarenessUpsell
			{...(({ product, cloudId, edition, userRoles }) => ({
				product,
				cloudId,
				edition,
				userRoles,
				hasDismissedButton: isDismissed,
				onDismissButton: handleDismiss,
			}))(props)}
		/>
	) : (
		<EditionAwarenessImpl {...props} />
	);
};

const EditionAwarenessPlatform = () => {
	const cloudId = useCloudId();
	const accountId = useAccountId();
	const environment = useEnvironment();
	const isSiteAdmin = useIsSiteAdmin();
	const isJiraAdmin = useIsAdmin();
	const { product, edition } = useProductAndEdition();
	const isFedRampEnv = isFedRamp();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	// Planned to be expanded as we add new actions
	const getAction = useCallback(async () => null, []);

	const userRoles: UserRole[] = useMemo(() => {
		const roles: UserRole[] = [];
		if (isSiteAdmin) {
			roles.push('SITE_ADMIN');
		}
		if (isJiraAdmin) {
			roles.push('JIRA_ADMIN');
		}
		return roles;
	}, [isSiteAdmin, isJiraAdmin]);

	const [siteTraits, setSiteTraits] = useState<Trait[] | undefined>(() => {
		// Calculate which editions need to fetch traits
		if (edition === 'free' || (edition === 'standard' && product === 'jira')) {
			return undefined;
		}

		if (edition === 'standard' && fg('ea_fetch_traits_for_standard_edition')) {
			// enable trait checking for all products in standard
			return undefined;
		}

		return [];
	});

	useEffect(() => {
		if (siteTraits === undefined) {
			memoizedFetchSitePersonalization({ cloudId })
				.then((traits: Trait[]) => {
					setSiteTraits(traits);
				})
				.catch((e) => {
					setSiteTraits([]);
					const event = createAnalyticsEvent({});
					fireErrorAnalytics({
						event,
						meta: {
							id: 'editionAwarenessGrowthCohortTrait',
							packageName: 'edition-awareness',
							teamName: 'growth-tako',
						},
						error: e,
						sendToPrivacyUnsafeSplunk: true,
					});
				});
		}
	}, [cloudId, createAnalyticsEvent, edition, siteTraits]);

	// We need to wait for the siteType to be set before rendering the button
	const canRender = siteTraits !== undefined;

	const { showFlag, dismissFlag } = useFlagsService();

	const handleShowFlag = useCallback(
		(flag: Flag) => {
			const { appearance, id, ...flagArgs } = flag;

			const type: FlagType = !appearance || appearance === 'normal' ? 'info' : appearance;

			showFlag({
				...flagArgs,
				// Assumes that when we're showing flags from edition awareness they are transactional
				messageType: 'transactional',
				messageId: id,
				type,
			});
		},
		[showFlag],
	);

	const editionAwarenessProps: EditionAwarenessProps | null = useMemo(() => {
		if (!product || !edition) {
			return null;
		}

		if (!accountId) {
			// We don't render when the customer is not logged in
			return null;
		}
		return {
			edition,
			cloudId,
			product,
			accountId,
			environment,
			userRoles,
			siteTraits,
			canRender,
			getAction,
			showFlag: handleShowFlag,
			hideFlag: dismissFlag,
		};
	}, [
		accountId,
		canRender,
		cloudId,
		edition,
		environment,
		userRoles,
		product,
		siteTraits,
		getAction,
		handleShowFlag,
		dismissFlag,
	]);

	if (!editionAwarenessProps) {
		return null;
	}

	if (isFedRampEnv) {
		return null;
	}

	return (
		<ContextualAnalyticsData
			attributes={{
				isSiteAdmin,
				product,
				edition,
			}}
		>
			{edition === 'premium' && product === 'jira' ? (
				// We need to call a few hooks to check RT, could be cleaned up in the future
				<EditionAwarenessPlatformInnerWithRTCheck {...editionAwarenessProps} />
			) : (
				<EditionAwarenessPlatformInner {...editionAwarenessProps} />
			)}
		</ContextualAnalyticsData>
	);
};

export const EditionAwareness = (): React.JSX.Element | null => {
	// This component is not rendered on the server, we don't have enough information to determine if it will be renderer or not
	// Don't remove because it may cause the SSR to fail, at this point it because the EditonAwarenessButton is using useLazyLoadQuery
	if (__SERVER__ && !fg('jfp-magma-4013-fix-hydration-edition-awareness')) {
		return null;
	}

	return (
		<RenderOnClient fg="jfp-magma-4013-fix-hydration-edition-awareness">
			<UFOSegment name="edition-awareness">
				<JSErrorBoundary
					id="EditionAwareness"
					packageName="jiraNavigationAppsSidebarEditionAwareness"
					fallback="unmount"
					teamName="growth-tako"
				>
					<EditionAwarenessPlatform />
				</JSErrorBoundary>
			</UFOSegment>
		</RenderOnClient>
	);
};
